import {
  ApiError,
  routesControllerArchivePrecheck,
  routesControllerArchiveRoute,
  routesControllerCreateRoute,
  routesControllerGetRoute,
  routesControllerListRoutes,
  routesControllerUnarchiveRoute,
  routesControllerUpdatePrecheck,
  routesControllerUpdateRoute,
  type CreateRouteDto,
  type RouteArchivePrecheckDto,
  type RouteArchiveResultDto,
  type RouteDetailDto,
  type RouteListItemDto,
  type RouteUpdatePrecheckDto,
  type UpdateRouteDto,
} from '@artic/api-client';
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { expectData } from '../_shared/api';
import { PRODUCTION_PLANNER_KEY } from '../production-planner/queries';
import { PROJECT_PLANNING_KEY } from '../project-planning/queries';

export const ROUTES_KEY = 'routing' as const;

// ── Conflict surface (409 → field-pinned error) ───────────────────────────────
//
// Mirrors features/factory-admin/queries.ts: a duplicate active route name
// returns 409 { field: 'route_name', message } and the editor pins the message
// to the name input instead of showing a generic failure.

export class RouteConflictError extends Error {
  public readonly field: 'route_name';

  constructor(message: string) {
    super(message);
    this.name = 'RouteConflictError';
    this.field = 'route_name';
  }
}

function mapConflict(err: unknown): never {
  if (err instanceof ApiError && err.status === 409) {
    const body = err.bodyAsRecord;
    const rawField = body?.['field'];
    const rawMessage = body?.['message'];
    const message = typeof rawMessage === 'string' ? rawMessage : err.message;
    if (rawField === 'route_name') {
      throw new RouteConflictError(message);
    }
  }
  throw err;
}

// ── Queries ───────────────────────────────────────────────────────────────────

export function routesListQueryOptions(partnerId: string | undefined, includeArchived = false) {
  return queryOptions({
    queryKey: [ROUTES_KEY, 'list', partnerId, includeArchived] as const,
    queryFn: async ({ signal }) => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await routesControllerListRoutes(
        partnerId,
        includeArchived ? { include_archived: 'true' } : undefined,
        { signal },
      );
      return expectData<RouteListItemDto[]>(res);
    },
    enabled: !!partnerId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function routeDetailQueryOptions(
  partnerId: string | undefined,
  routeId: string | undefined,
) {
  return queryOptions({
    queryKey: [ROUTES_KEY, 'detail', partnerId, routeId] as const,
    queryFn: async ({ signal }) => {
      if (!partnerId || !routeId) throw new Error('partnerId and routeId are required');
      const res = await routesControllerGetRoute(partnerId, routeId, { signal });
      return expectData<RouteDetailDto>(res);
    },
    enabled: !!partnerId && !!routeId,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Archive pre-check counts. `staleTime: 0` so every modal open reflects the
 * work orders' current statuses (a stale count here misleads the user about
 * what the archive will clear).
 */
export function routeArchivePrecheckQueryOptions(
  partnerId: string | undefined,
  routeId: string | undefined,
) {
  return queryOptions({
    queryKey: [ROUTES_KEY, 'archive-precheck', partnerId, routeId] as const,
    queryFn: async ({ signal }) => {
      if (!partnerId || !routeId) throw new Error('partnerId and routeId are required');
      const res = await routesControllerArchivePrecheck(partnerId, routeId, { signal });
      return expectData<RouteArchivePrecheckDto>(res);
    },
    enabled: !!partnerId && !!routeId,
    staleTime: 0,
    placeholderData: keepPreviousData,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────────
//
// Every mutation invalidates the whole ROUTES_KEY family (lists across both
// include_archived variants + details) — route CRUD is rare enough that the
// blanket refetch is cheaper than tracking which keys each write touches.

function useInvalidateRoutes() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: [ROUTES_KEY] });
    // ENG-743: scope narrowing, default moves and archiving clear routes /
    // drafting factories on work orders server-side — both planning grids
    // display those fields, so refresh them too.
    void queryClient.invalidateQueries({ queryKey: [PRODUCTION_PLANNER_KEY] });
    void queryClient.invalidateQueries({ queryKey: [PROJECT_PLANNING_KEY] });
  };
}

/**
 * ENG-743 §14/§15 dry run: what the proposed PATCH would clear. A mutation
 * (not a query) — it takes the proposed body and is called once per save
 * attempt, so caching semantics don't apply.
 */
export function useRouteUpdatePrecheck(partnerId: string | undefined) {
  return useMutation({
    mutationFn: async ({ routeId, input }: { routeId: string; input: UpdateRouteDto }) => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await routesControllerUpdatePrecheck(partnerId, routeId, input);
      return expectData<RouteUpdatePrecheckDto>(res);
    },
  });
}

export function useCreateRoute(partnerId: string | undefined) {
  const invalidate = useInvalidateRoutes();
  return useMutation({
    mutationFn: async (input: CreateRouteDto) => {
      if (!partnerId) throw new Error('partnerId is required');
      try {
        const res = await routesControllerCreateRoute(partnerId, input);
        return expectData<RouteDetailDto>(res);
      } catch (err) {
        mapConflict(err);
      }
    },
    onSuccess: () => invalidate(),
  });
}

export function useUpdateRoute(partnerId: string | undefined) {
  const invalidate = useInvalidateRoutes();
  return useMutation({
    mutationFn: async ({ routeId, input }: { routeId: string; input: UpdateRouteDto }) => {
      if (!partnerId) throw new Error('partnerId is required');
      try {
        const res = await routesControllerUpdateRoute(partnerId, routeId, input);
        return expectData<RouteDetailDto>(res);
      } catch (err) {
        mapConflict(err);
      }
    },
    onSuccess: () => invalidate(),
  });
}

export function useArchiveRoute(partnerId: string | undefined) {
  const invalidate = useInvalidateRoutes();
  return useMutation({
    mutationFn: async (routeId: string) => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await routesControllerArchiveRoute(partnerId, routeId);
      return expectData<RouteArchiveResultDto>(res);
    },
    onSuccess: () => invalidate(),
  });
}

export function useUnarchiveRoute(partnerId: string | undefined) {
  const invalidate = useInvalidateRoutes();
  return useMutation({
    mutationFn: async (routeId: string) => {
      if (!partnerId) throw new Error('partnerId is required');
      try {
        const res = await routesControllerUnarchiveRoute(partnerId, routeId);
        return expectData<RouteDetailDto>(res);
      } catch (err) {
        // Unarchiving re-enters the active-name uniqueness — another active
        // route with the same name 409s just like create/rename.
        mapConflict(err);
      }
    },
    onSuccess: () => invalidate(),
  });
}
